import { ReactElement, useEffect, useState } from 'react'
import { Button, Descriptions, Modal, Space, Typography, message, theme } from 'antd'
import { CheckOutlined, InfoCircleOutlined, CheckCircleFilled } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useBook, useCreateDraftOrder, useCompleteDraftOrder, useCreateDraftOrderWithoutBooking, useCurrentLocation, useReschedule } from 'api'
import { Config, Settings, Appointment, Feature } from 'types'
import { useTranslate, getTimeFromDateTimeString, isNil, queryClient, getCustomerName } from 'utils'
import { BookingAction, BookingPageDispatch, BookingPageState } from 'pages/booking/booking-page/booking-page'
import { HorizontalContainer } from 'components/styled'

const { Paragraph } = Typography

type BookingModalProps = {
    isOpen: boolean
    pageState: BookingPageState
    config: Config
    settings: Settings
    appointment?: Appointment
    pageStateDispatch: BookingPageDispatch
    onCancel: () => void
}

type BookingStep = 'beforeBook' | 'afterBook' | 'afterDraftCreated' | 'afterDraftCompleted'

export const BookingModal = ({ isOpen, pageState, config, settings, appointment, pageStateDispatch, onCancel}: BookingModalProps) => {
    const { __ } = useTranslate()
    const navigate = useNavigate()
    const { token: { margin, fontSize, fontSizeXL, colorInfo, colorSuccess } } = theme.useToken()
    const [bookingStep, setBookingStep] = useState<BookingStep>('beforeBook')
    const isReschedule = !isNil(appointment)
    const { data: currentLocation } = useCurrentLocation()
    const timezone = currentLocation ? currentLocation.timezone : config ? config.settings.timezone : null

    const canCreateDraftOrderWithoutBooking = config.features.includes(Feature.ADMIN_DRAFT_ORDER_WITHOUT_BOOKING)

    const { mutate: book, isLoading: bookIsLoading, data: bookResponse } = useBook(
        pageState.service?.id ?? '',
        pageState.variant?.id ?? '',
        pageState.participants,
        pageState.resources.map(r => r?.id ?? ''),
        pageState.customer?.id ?? '',
        new Date(pageState.slot?.startDateTime ?? '1991-04-18 00:00'),
        getTimeFromDateTimeString(pageState.slot?.startDateTime ?? '') ?? '',
        timezone
    )

    const { mutate: createDraftOrder, isLoading: createDraftOrderIsLoading, data: createDraftOrderResponse } = useCreateDraftOrder(
        bookResponse || '',
        pageState.resources.map(r => r?.id ?? '')
    )

    const { mutate: createDraftOrderWithoutBooking, isLoading: createDraftOrderWithoutBookingIsLoading, data: createDraftOrderWithoutBookingResponse } = useCreateDraftOrderWithoutBooking(
        pageState.service?.id ?? '',
        pageState.variant?.id ?? '',
        pageState.participants,
        pageState.resources.map(r => r?.id ?? ''),
        pageState.customer?.id ?? '',
        new Date(pageState.slot?.startDateTime ?? '1991-04-18 00:00'),
        getTimeFromDateTimeString(pageState.slot?.startDateTime ?? '') ?? ''
    )

    const { mutate: completeOrder, isLoading: completeDraftOrderIsLoading, data: completeDraftOrderResponse } = useCompleteDraftOrder(createDraftOrderWithoutBookingResponse || createDraftOrderResponse || '')

    const { mutate: reschedule, isLoading: rescheduleIsLoading, data: rescheduleResponse } = useReschedule(
        appointment?.id ?? '',
        pageState.resources.map(resource => resource?.id ?? ''),
        pageState.slot?.startDateTime ?? '',
        timezone
    )

    const resetSlot = () => {
        // formIsDirty is tied to the slot value. It's necessary to reset its value
        // in order to prevent showing unnecessary unsaved form changes modal.
        pageStateDispatch({
            type: BookingAction.SET_SLOT,
            payload: { slot: null }
        })
    }

    useEffect(() => {
        if (bookResponse) {
            setBookingStep('afterBook')
            queryClient.invalidateQueries(['bookingSlots'])
            resetSlot()
        }
    }, [bookIsLoading])

    useEffect(() => {
        if (createDraftOrderWithoutBookingResponse) {
            setBookingStep('afterDraftCreated')
            resetSlot()
        }
    }, [createDraftOrderWithoutBookingIsLoading])

    useEffect(() => {
        createDraftOrderResponse && setBookingStep('afterDraftCreated')
    }, [createDraftOrderIsLoading])

    useEffect(() => {
        if (completeDraftOrderResponse) {
            setBookingStep('afterDraftCompleted')
        }
    }, [completeDraftOrderIsLoading])

    useEffect(() => {
        if (rescheduleResponse) {
            onClose()
            resetSlot()
            message.success(__`appointment_saved`)
            queryClient.invalidateQueries(['bookingSlots'])
            navigate('/calendar')
        }
    }, [rescheduleIsLoading])
    
    const getDate = (datetime: string): string => {
        if (!datetime) {
            return ''
        }

        const dateObj = new Date(datetime)

        return dateObj.toLocaleString([config.locale, 'en-US'], {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            weekday: 'short',
        })
    }

    const getTime = (datetime: string): string => {
        if (!datetime) {
            return ''
        }

        const dateObj = new Date(datetime)

        return dateObj.toLocaleString([config.locale, 'en-US'], {
            hour: 'numeric',
            minute: '2-digit',
            hour12: settings.timeFormat === '12' ? true : false
        })
    }

    const getModalTitle = (): ReactElement => {
        let title: string = ''
        switch (bookingStep) {
            case 'beforeBook':
                title = isReschedule ? __`reschedule_reassign_confirmation` : __`book_confirmation`
                break
            case 'afterBook':
                title = __`booked_successfully`
                break
            case 'afterDraftCreated':
                title = __`draft_order_created`
                break
            case 'afterDraftCompleted':
                title = __`draft_order_completed`
                break
        }
        return (
            <Space size='small'>
                {bookingStep === 'beforeBook' ? <InfoCircleOutlined style={{ color: colorInfo, fontSize: fontSizeXL }} /> : <CheckCircleFilled style={{ color: colorSuccess, fontSize: fontSizeXL }} />}
                {title}
            </Space>
        )
    }

    const getModalContent = (): ReactElement => {
        switch (bookingStep) {
            case 'beforeBook':
                return (
                    <Descriptions column={1} size='small' style={{ marginTop: margin }} bordered>
                        <Descriptions.Item label={__`service`}>{ pageState.service?.title }</Descriptions.Item>
                        <Descriptions.Item label={__`variant`}>{ pageState.variant?.title }</Descriptions.Item>
                        <Descriptions.Item label={__`participants`}>{ pageState.participants }</Descriptions.Item>
                        <Descriptions.Item label={__`resources`}>
                            {pageState.resources?.map((resource, index) => (
                                <div key={index}>{resource?.name}</div>
                            ))}
                        </Descriptions.Item>
                        <Descriptions.Item label={__`customer`}>{getCustomerName(pageState.customer)}</Descriptions.Item>
                        <Descriptions.Item label={__`date`}>{ getDate(pageState.slot?.startDateTime || '') }</Descriptions.Item>
                        <Descriptions.Item label={__`time`}>{ getTime(pageState.slot?.startDateTime || '') }</Descriptions.Item>
                    </Descriptions>
                )
            case 'afterBook':
                return (<Paragraph>{ __`to_checkout_add_this_booking_to_the_cart_in_pos_or_to_a_draft_order_to_send_an_invoice_for_payment` }</Paragraph>)
            case 'afterDraftCreated':
                return (<Paragraph>{ __`mark_as_paid_or_send_an_invoice_appointment_will_be_booked_as_soon_as_the_order_is_completed` }</Paragraph>)
            case 'afterDraftCompleted':
                return (<Paragraph>{ __`mark_as_paid_or_send_an_invoice_appointment_will_be_booked_as_soon_as_the_order_is_completed` }</Paragraph>)
        }
    }

    const getModalPrimaryButton = (): ReactElement => {
        switch (bookingStep) {
            case 'beforeBook':
                return (
                    <Button
                        type='primary'
                        size='large'
                        icon={<CheckOutlined style={{ fontSize }}/>}
                        onClick={() => isReschedule ? reschedule() : book()}
                        loading={rescheduleIsLoading || bookIsLoading}
                    >
                        {__`book`}
                    </Button>
                )
            case 'afterBook':
                return (
                    <Button
                        type='primary'
                        size='large'
                        icon={<CheckOutlined style={{ fontSize }}/>}
                        onClick={() => createDraftOrder()}
                        loading={createDraftOrderIsLoading}
                    >
                        {__`create_draft_order`}
                    </Button>
                )
            case 'afterDraftCreated':
                return (
                    <Button
                        type='primary'
                        size='large'
                        icon={<CheckOutlined style={{ fontSize }}/>}
                        onClick={() => completeOrder()}
                        loading={completeDraftOrderIsLoading}
                    >
                        {__`complete_draft_order`}
                    </Button>
                )
            case 'afterDraftCompleted':
                return (
                    <Button
                        type='primary'
                        size='large'
                        onClick={() => navigate('/calendar')}
                        loading={completeDraftOrderIsLoading}
                    >
                        {__`view_booking`}
                    </Button>
                )
        }
    }

    const getModalSecondaryButton = (): ReactElement => {
        switch (bookingStep) {
            case 'beforeBook':
                return (
                    <Button
                        type='text'
                        size='large'
                        onClick={() => canCreateDraftOrderWithoutBooking ? createDraftOrderWithoutBooking() : onClose()}
                        loading={canCreateDraftOrderWithoutBooking && createDraftOrderWithoutBookingIsLoading}
                    >
                        {canCreateDraftOrderWithoutBooking ? __`save_draft_order` : __`cancel`}
                    </Button>
                )
            case 'afterBook':
            case 'afterDraftCreated':
                return (
                    <Button
                        type='text'
                        size='large'
                        onClick={() => navigate('/calendar')}
                    >
                        {__`view_booking`}
                    </Button>
                )
            case 'afterDraftCompleted':
                return (
                    <Button
                        type='text'
                        size='large'
                        onClick={onClose}
                    >
                        {__`book_another`}
                    </Button>
                )
        }
    }

    const onClose = (): void => {
        setBookingStep('beforeBook')
        onCancel()
    }

    return (
        <Modal
            title={getModalTitle()}
            open={isOpen}
            footer={null}
            onCancel={onClose}
            maskClosable={bookingStep === 'beforeBook'}
            destroyOnClose
        >
            {getModalContent()}
            <HorizontalContainer style={{ justifyContent: 'flex-end', marginTop: margin }}>
                { getModalSecondaryButton() }
                { getModalPrimaryButton() }
            </HorizontalContainer>
        </Modal>
    )
}
